import moment from 'moment';
import 'moment/locale/de';
import 'moment/locale/en-gb';

/**
 * Single place where the app language is mapped onto date-formatting locales.
 * This is the only module allowed to import `moment/locale/*` or call
 * `moment.locale()` - those imports set moment's global locale as a side effect
 * of `defineLocale`, so scattering them means whoever loads last wins.
 *
 * English maps to en-GB, not en. Two reasons:
 * - moment's built-in `en` is American, so `l` renders M/D/YYYY and `LT`
 *   renders 12-hour times, which reads as wrong in a European product.
 * - en-GB gives day-first dates and 24-hour times, matching the German UI's
 *   shape so the two languages stay comparable at a glance.
 * A US user therefore sees 16/09/2019 rather than 9/16/2019. That is deliberate.
 */
const MOMENT_LOCALES: Record<string, string> = {
  de: 'de',
  en: 'en-gb',
};

const INTL_LOCALES: Record<string, string> = {
  de: 'de-DE',
  en: 'en-GB',
};

const DEFAULT_LANGUAGE = 'de'; // matches i18n fallbackLng

/**
 * `i18n.language` is already normalised to 'de' / 'en' (see assets/i18n/i18n),
 * so this is only a guard for callers passing something else - a raw
 * `navigator.language`, a stored preference, a stale cached value.
 */
const toLanguageCode = (language?: string) => (language ?? '').split('-')[0].toLowerCase();

/**
 * Locale to pass to `Intl` / `toLocaleDateString` and friends.
 */
export const getIntlLocale = (language?: string) =>
  INTL_LOCALES[toLanguageCode(language)] ?? INTL_LOCALES[DEFAULT_LANGUAGE];

/**
 * Points moment at the locale matching the given app language.
 */
export const syncMomentLocale = (language?: string) => {
  moment.locale(MOMENT_LOCALES[toLanguageCode(language)] ?? MOMENT_LOCALES[DEFAULT_LANGUAGE]);
};
